import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'
import { ReactSVG } from 'react-svg'

import { useAddressStore } from '@features/shipping-methods'

import { ProductPreviewCard } from '@widgets/product-preview'


import { Product, useFavoriteProductsCardsQuery } from '@shared/generated'
import CardSkeleton from '@shared/ui/card-skeleton'

import styles from './styles.module.scss'

const LIMIT = 10

function ProductsLoading ({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <CardSkeleton key={index} />
      ))}
    </>
  )
}

export default function Favorites () {
  const { shopId } = useAddressStore()
  const { t: tp } = useTranslation('personal')
  const { t: tf } = useTranslation('favorites')
  const { data, loading, fetchMore } = useFavoriteProductsCardsQuery({
    variables: {
      shopId: shopId,
      pager: { page: 1, limit: LIMIT },
    },
    notifyOnNetworkStatusChange: true,
  })

  const products = useMemo(() => {
    return (data?.favoriteProducts?.edges ?? []).filter(
      (item): item is Product => !!item,
    )
  }, [ data ])

  const total = data?.favoriteProducts?.pageInfo?.total ?? 0
  const currentPage = data?.favoriteProducts?.pageInfo?.page ?? 1
  const lastPage = data?.favoriteProducts?.pageInfo?.lastPage ?? 1

  const hasMore = currentPage < lastPage

  const loadMore = () => {
    if (!hasMore || loading) return

    fetchMore({
      variables: {
        pager: { page: currentPage + 1, limit: LIMIT },
        shopId: shopId,
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.favoriteProducts?.edges?.length) {
          return prevResult
        }

        return {
          ...fetchMoreResult,
          favoriteProducts: {
            ...fetchMoreResult.favoriteProducts,
            edges: [
              ...(prevResult.favoriteProducts?.edges ?? []),
              ...(fetchMoreResult.favoriteProducts?.edges ?? []),
            ],
          },
        }
      },
    })
  }

  if (loading && !products) {
    return (
      <div className={styles.root}>
        <div className={styles.rootRow}>
          {Array.from({ length: LIMIT }).map((_, index) => (
            <CardSkeleton key={index} />
          ))}
        </div>
      </div>
    )
  }

  if (!loading && !products.length) {
    return (
      <div className={styles.root}>
        <div className={styles.emptyRoot}>
          <h2 className={styles.title}>{tp('nav.favorites')}</h2>
          <p>{tf('emptyTitle')}</p>
          <p className={styles.emptyFavorites}>
            <span>{tf('emptySubTitle')}</span>
            <ReactSVG src="/icons/heart.svg" wrapper="span" />
          </p>
        </div>
      </div>
    )
  }

  return (
    <section className={styles.root}>
      <h2 className={styles.title}>{tp('nav.favorites')}</h2>
      <InfiniteScroll
        dataLength={products.length}
        next={loadMore}
        hasMore={hasMore}
        loader={
          <ProductsLoading count={Math.min(LIMIT, total - products.length)} />
        }
        className={styles.rootRow}
      >
        {products.map((product) => (
          <ProductPreviewCard
            key={product.id}
            className={styles.card}
            data={product as Product}
          />
        ))}
      </InfiniteScroll>
    </section>
  )
}
